import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import SEO from "@/components/site/SEO";
import { Lock, Inbox, Mail, Phone, Gift, Trophy, RefreshCw } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Admin() {
  const [key, setKey] = useState("");
  const [authed, setAuthed] = useState(false);
  const [tab, setTab] = useState("contacts"); // contacts | waitlist
  const [contacts, setContacts] = useState([]);
  const [waitlist, setWaitlist] = useState([]);
  const [loading, setLoading] = useState(false);

  const loadAll = async (k) => {
    setLoading(true);
    try {
      const [c, w] = await Promise.all([
        axios.get(`${API}/contact?admin_key=${encodeURIComponent(k)}`),
        axios.get(`${API}/waitlist?admin_key=${encodeURIComponent(k)}`),
      ]);
      setContacts(c.data || []);
      setWaitlist(w.data || []);
      setAuthed(true);
      toast.success(`Loaded ${c.data.length} contacts · ${w.data.length} waitlist entries`);
    } catch (err) {
      toast.error("Invalid admin key or server error.");
      setAuthed(false);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="page-admin">
      <SEO title="Admin" description="SECQRE admin console" path="/admin" />
      <section className="pt-32 md:pt-40 pb-24">
        <div className="container-secqre max-w-5xl">
          <span className="overline">— Admin</span>
          <h1 className="mt-4 font-heading font-black text-4xl md:text-5xl tracking-tight" data-testid="admin-title">Console</h1>

          {!authed ? (
            <form
              onSubmit={(e) => { e.preventDefault(); loadAll(key); }}
              className="mt-10 rounded-2xl border border-[#E2E8F0] bg-white p-8 max-w-md"
              data-testid="admin-auth-form"
            >
              <div className="w-11 h-11 rounded-lg bg-[#F8F9FA] inline-flex items-center justify-center">
                <Lock className="w-5 h-5 text-[#0F172A]" />
              </div>
              <h2 className="mt-4 font-heading font-bold text-xl">Sign in</h2>
              <p className="text-sm text-[#475569] mt-1">Enter the admin key to view submissions.</p>
              <input
                type="password"
                value={key}
                onChange={(e) => setKey(e.target.value)}
                placeholder="Admin key"
                className="mt-5 w-full px-4 py-3 rounded-lg border border-[#E2E8F0] focus:outline-none focus:ring-2 focus:ring-[#0F172A]/20"
                data-testid="admin-key-input"
              />
              <button type="submit" disabled={loading} className="btn-primary mt-5 w-full" data-testid="admin-signin">
                {loading ? "Checking..." : "Sign in"}
              </button>
              <p className="mt-3 text-xs text-[#94a3b8]">Default dev key: <code className="bg-[#F8F9FA] px-1.5 py-0.5 rounded">secqre-admin-2026</code></p>
            </form>
          ) : (
            <div className="mt-10" data-testid="admin-dashboard">
              <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
                <div className="inline-flex rounded-lg border border-[#E2E8F0] bg-white p-1" role="tablist">
                  <TabBtn active={tab === "contacts"} onClick={() => setTab("contacts")} testid="admin-tab-contacts">
                    <Inbox className="w-4 h-4" /> Contacts <Pill>{contacts.length}</Pill>
                  </TabBtn>
                  <TabBtn active={tab === "waitlist"} onClick={() => setTab("waitlist")} testid="admin-tab-waitlist">
                    <Trophy className="w-4 h-4" /> Honk Waitlist <Pill>{waitlist.length}</Pill>
                  </TabBtn>
                </div>
                <button onClick={() => loadAll(key)} disabled={loading} className="btn-ghost !py-2 !px-4 text-sm" data-testid="admin-refresh">
                  <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} /> Refresh
                </button>
              </div>

              {tab === "contacts" && (
                <div className="space-y-4" data-testid="admin-contacts">
                  {contacts.length === 0 && <EmptyState text="No contact submissions yet." />}
                  {contacts.map((it, i) => (
                    <div key={it.id || i} className="rounded-2xl border border-[#E2E8F0] bg-white p-6" data-testid={`admin-contact-${i}`}>
                      <div className="flex items-start justify-between gap-6 flex-wrap">
                        <div>
                          <h3 className="font-heading font-bold text-lg">{it.name}</h3>
                          <div className="mt-2 flex flex-wrap items-center gap-x-5 gap-y-1 text-sm text-[#475569]">
                            <span className="inline-flex items-center gap-1.5"><Mail className="w-3.5 h-3.5" />{it.email}</span>
                            {it.phone && <span className="inline-flex items-center gap-1.5"><Phone className="w-3.5 h-3.5" />{it.phone}</span>}
                          </div>
                        </div>
                        <span className="text-xs text-[#94a3b8] font-mono">
                          {it.created_at && new Date(it.created_at).toLocaleString()}
                        </span>
                      </div>
                      {it.subject && <p className="mt-4 text-sm font-semibold text-[#0A0A0A]">Subject: {it.subject}</p>}
                      <p className="mt-3 text-[#0A0A0A] whitespace-pre-wrap">{it.message}</p>
                    </div>
                  ))}
                </div>
              )}

              {tab === "waitlist" && (
                <div className="space-y-4" data-testid="admin-waitlist">
                  {waitlist.length === 0 && <EmptyState text="No Honk Patrol unlocks yet — drive some players to /play." />}
                  {waitlist.map((it, i) => (
                    <div key={it.id || i} className="rounded-2xl border border-[#E2E8F0] bg-white p-6 flex flex-wrap items-center gap-6" data-testid={`admin-waitlist-${i}`}>
                      <div className="w-12 h-12 rounded-lg bg-[#FFD600] inline-flex items-center justify-center shrink-0">
                        <Gift className="w-5 h-5 text-[#0A0A0A]" />
                      </div>
                      <div className="flex-1 min-w-[240px]">
                        <div className="font-mono font-bold text-base text-[#0A0A0A]">{it.code}</div>
                        <div className="text-sm text-[#475569] inline-flex items-center gap-1.5 mt-1">
                          <Mail className="w-3.5 h-3.5" />{it.email}
                        </div>
                      </div>
                      <div className="text-right">
                        <div className="font-heading font-bold text-xl">{it.score} pts</div>
                        <div className="text-xs text-[#94a3b8] font-mono mt-1">
                          {it.created_at && new Date(it.created_at).toLocaleString()}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      </section>
    </div>
  );
}

function TabBtn({ active, onClick, children, testid }) {
  return (
    <button
      onClick={onClick}
      data-testid={testid}
      className={`inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-md text-sm font-medium transition-colors ${
        active ? "bg-[#0F172A] text-white" : "text-[#475569] hover:text-[#0A0A0A]"
      }`}
    >
      {children}
    </button>
  );
}

function Pill({ children }) {
  return <span className="ml-1 inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 rounded-full bg-[#FFD600] text-[#0A0A0A] text-[10px] font-bold tabular-nums">{children}</span>;
}

function EmptyState({ text }) {
  return <div className="rounded-2xl border border-dashed border-[#E2E8F0] p-12 text-center text-[#475569]">{text}</div>;
}
